// src/domain/models/task/taskProxy.js
/**
 * @fileoverview Defines the Proxy handler for Task instances to manage extended
 * attributes dynamically.
 */

import { getPrivateData } from '@/domain/models/privateMemory.js';
import { parseISODateField } from '@/i12e/utilities/dateToolkit.js';
import dayjs from 'dayjs';

/**
 * Retrieves the value of an extended attribute.
 * If the attribute ends with '_on' or '_at', it is parsed as a date/datetime.
 *
 * @param {Object} target - The Task instance.
 * @param {string} prop - The extended attribute name.
 * @returns {dayjs.Dayjs|string|undefined} - The parsed date/datetime object,
 * raw value, or undefined if not found.
 */
const getExtendedAttribute = (target, prop) => {
  const data = getPrivateData(target).parsed;
  if (!(prop in data)) return undefined;
  const value = data[prop];
  if (prop.endsWith('_on') || prop.endsWith('_at')) {
    return parseISODateField(value);
  }
  return value;
};

/**
 * Sets the value of an extended attribute.
 * Validates the value if the attribute name ends with '_on' or '_at'.
 *
 * @param {Object} target - The Task instance.
 * @param {string} prop - The extended attribute name.
 * @param {any} value - The value to set.
 * @returns {boolean} - Returns true if the operation was successful.
 * @throws {Error} - Throws if validation fails for date/datetime attributes.
 */
const setExtendedAttribute = (target, prop, value) => {
  if (
    (prop.endsWith('_on') || prop.endsWith('_at')) &&
    (typeof value !== 'string' || !dayjs(value).isValid())
  ) {
    throw new Error(
      `Value for ${prop} must be a valid ISO 8601 date/datetime string.`
    );
  }

  const data = getPrivateData(target).parsed;
  data[prop] = value;
  return true;
};

/**
 * Creates a Proxy for a Task instance to handle extended attributes.
 *
 * @param {Object} instance - The Task instance to be proxied.
 * @returns {Proxy} - The proxied Task instance.
 */
export function createTaskProxy(instance) {
  return new Proxy(instance, {
    /**
     * Intercepts property access on the Task instance.
     * Core attributes and methods are accessed directly, while extended
     * attributes are handled via the Proxy.
     *
     * @param {Object} target - The Task instance.
     * @param {string|symbol} prop - The property name being accessed.
     * @param {Object} receiver - The Proxy or object that made the request.
     * @returns {any} - The value of the property or extended attribute.
     */
    get(target, prop, receiver) {
      if (Reflect.has(target, prop)) {
        const value = Reflect.get(target, prop, receiver);
        return value;
      }
      return getExtendedAttribute(receiver, prop);
    },

    /**
     * Intercepts property assignment on the Task instance.
     * Core attributes and methods are set directly, while extended
     * attributes are handled via the Proxy.
     *
     * @param {Object} target - The Task instance.
     * @param {string|symbol} prop - The property name being set.
     * @param {any} value - The value to assign to the property.
     * @param {Object} receiver - The Proxy or object that made the request.
     * @returns {boolean} - Returns true if the assignment was successful.
     * @throws {Error} - Throws if validation fails for date/datetime
     * extended attributes.
     */
    set(target, prop, value, receiver) {
      if (Reflect.has(target, prop)) {
        return Reflect.set(target, prop, value, receiver);
      }
      return setExtendedAttribute(receiver, prop, value);
    }
  });
}
